import { createAsyncThunk } from "@reduxjs/toolkit";
import { organizationAPI } from "libs/apiClient/Organization";
import { snackWrapper } from "helpers";

export const getFolders = createAsyncThunk(
	"foldersSlice/getFolders",
	async ({ organizationId, archived }: { organizationId: number; archived: boolean }, { rejectWithValue }) => {
		try {
			return await organizationAPI.foldersDetail(organizationId, { archive: archived }).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getDocumentList = createAsyncThunk(
	"foldersSlice/getDocumentList",
	async ({ folderId, archived }: { folderId: number; archived: boolean }, { rejectWithValue }) => {
		try {
			return await organizationAPI.folderDocsDetail(folderId, { archived }).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const uploadDocument = createAsyncThunk(
	"foldersSlice/uploadDocument",
	async (
		{
			name,
			folderId,
			file,
			callBack,
			handler,
		}: { name: string; folderId: number; file: any; callBack: any; handler: boolean },
		{ rejectWithValue },
	) => {
		try {
			const params = new FormData();
			params.append("file", file);
			params.append("params", JSON.stringify({ name: name }));
			const response = await organizationAPI.folderDocsCreate(folderId, { file }).then((r) => r.data);
			callBack();
			return { response, handler };
		} catch (error: any) {
			snackWrapper("error", error.response.data);
			callBack();
			return rejectWithValue(error.response.data);
		}
	},
);

export const updateDocument = createAsyncThunk(
	"foldersSlice/updateDocument",
	async ({ docId, file }: { docId: number; file: any }, { rejectWithValue }) => {
		try {
			const params = new FormData();
			params.append("file", file);
			const response = await organizationAPI.folderDocCreate(docId, { file }).then((r) => r.data);
			return { docId, response };
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getVersionList = createAsyncThunk(
	"foldersSlice/getVersionList",
	async ({ docId, archived }: { docId: number; archived: boolean }, { rejectWithValue }) => {
		try {
			return await organizationAPI.folderDocVersionsDetail(docId, { archived }).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const updateDocumentVersion = createAsyncThunk(
	"foldersSlice/updateDocumentVersion",
	async ({ targetId, docId }: { targetId: number; docId: number }, { rejectWithValue }) => {
		try {
			const response = await organizationAPI.folderDocVersionCreate(docId).then((r) => r.data);
			return { targetId, response };
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const createFolder = createAsyncThunk(
	"foldersSlice/createFolder",
	async (
		{
			organizationId,
			folderName,
			close,
			showWarning,
		}: { organizationId: number; folderName: string; close: () => void; showWarning: () => void },
		{ rejectWithValue },
	) => {
		try {
			const response = await organizationAPI.foldersCreate(organizationId, { name: folderName }).then((r) => r.data);
			close();
			return response;
		} catch (e: any) {
			if (e.response.status === 404) showWarning();
			return rejectWithValue(e.response.data);
		}
	},
);

export const editFolder = createAsyncThunk(
	"foldersSlice/editFolder",
	async (
		{
			folderId,
			folderName,
			close,
			showWarning,
		}: { folderId: number; folderName: string; close: () => void; showWarning: () => void },
		{ rejectWithValue },
	) => {
		try {
			const response = await organizationAPI.folderUpdate(folderId, { name: folderName }).then((r) => r.data);
			close();
			return response;
		} catch (e: any) {
			if (e.response.status === 404) showWarning();
			return rejectWithValue(e.response.data);
		}
	},
);

export const editDocument = createAsyncThunk(
	"foldersSlice/editDocument",
	async (
		{
			docId,
			folderName,
			close,
			showWarning,
		}: { docId: number; folderName: string; close: () => void; showWarning: () => void },
		{ rejectWithValue },
	) => {
		try {
			const response = await organizationAPI.folderDocUpdate(docId, folderName).then((r) => r.data);
			close();
			return response;
		} catch (e: any) {
			if (e.response.status === 404) showWarning();
			return rejectWithValue(e.response.data);
		}
	},
);

export const deleteFolder = createAsyncThunk(
	"foldersSlice/deleteFolder",
	async ({ folderId }: { folderId: number }, { rejectWithValue }) => {
		try {
			await organizationAPI.folderDelete(folderId, { archived: true });
			return folderId;
		} catch (error: any) {
			// ready
			snackWrapper("error", error.response.data);
		}
	},
);

export const deleteDocument = createAsyncThunk(
	"foldersSlice/deleteDocument",
	async ({ documentId }: { documentId: number }, { rejectWithValue }) => {
		try {
			await organizationAPI.folderDocDelete(documentId, { archived: true });
			return documentId;
		} catch (error: any) {
			snackWrapper("error", error.response.data);
		}
	},
);

export const restoreFolder = createAsyncThunk(
	"foldersSlice/restoreFolder",
	async ({ folderId }: { folderId: number }, { rejectWithValue }) => {
		try {
			await organizationAPI.folderDelete(folderId, { archived: false });
			return folderId;
		} catch (error: any) {
			snackWrapper("error", error.response.data);
		}
	},
);

export const restoreDocument = createAsyncThunk(
	"foldersSlice/restoreDocument",
	async ({ documentId }: { documentId: number }, { rejectWithValue }) => {
		try {
			await organizationAPI.folderDocDelete(documentId, { archived: false });
			return documentId;
		} catch (error: any) {
			snackWrapper("error", error.response.data);
		}
	},
);
